import { useEffect, useRef } from 'react'

import { type BusinessDetailsForm, useBusinessDetailsForm } from '@/components/Chat/useBusinessDetailsForm'
import { type OwnerInfoForm, useOwnerInfoForm } from '@/components/Chat/useOwnerInfoForm'
import { type TravelDistanceForm, useTravelDistanceForm } from '@/components/Chat/useTravelDistanceForm'
import type { Org } from '@/models/user'
import { type FillCardForm, useFillCardSave } from './useFillCardSave'
import { type FreightForm, useFreightForm } from './useFreightForm'

export interface BusinessCardForm extends FillCardForm {
  details: BusinessDetailsForm
  owner: OwnerInfoForm
  distance: TravelDistanceForm
  freight: FreightForm
}

export function useBusinessCardForm(orgId: string, organisation: Org): BusinessCardForm {
  const application = organisation.currentInsuranceApplication
  const details = useBusinessDetailsForm({
    business: organisation.business,
    entityType: application?.entityType,
    hasEld: application?.hasEld,
    eldProvider: application?.eldProvider,
    coverageLapseDetected: application?.coverageLapseDetected,
    cancellationReason: application?.cancellationReason,
  })
  const owner = useOwnerInfoForm(application?.businessOwner)
  const distance = useTravelDistanceForm(application?.travelDistance)
  const freight = useFreightForm(application?.commodities)
  // Page order, which is also the order the parts save in.
  const card = useFillCardSave(orgId, [details, owner, distance, freight])

  // Another person became the owner: edits typed for the last one must not be saved onto them.
  const ownerId = application?.businessOwner?.id
  const shownOwnerId = useRef(ownerId)
  useEffect(() => {
    if (shownOwnerId.current === ownerId) return
    shownOwnerId.current = ownerId
    owner.discard()
  }, [ownerId, owner])

  return { ...card, details, owner, distance, freight }
}
